import React, { useState, useRef, useEffect } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useEmbedMode } from '../hooks/useEmbedMode';

// 组件导入
import { ResourceTree } from '../components/ResourceTree';
import { Overview } from '../components/Overview';
import { OntologyDetail } from '../components/OntologyDetail';
import { ActionModals } from '../components/ActionModals';

// Hooks 和工具导入
import { useResourceTree, type OntologyNode } from '../hooks/useResourceTree';
import { useOntologyDetail } from '../hooks/useOntologyDetail';
import { ResourceHelpers } from '../utils/resourceHelpers';
import {
  createNewNode,
  addNodeToCorrectPosition,
  addNodeToDefaultOrRoot,
} from '../utils/resourceTreeUtils';
import { RESOURCE_NAME_MAX_LENGTH } from '../constants/resource';

// 添加自定义样式
const treeNodeStyles = `
  .ant-tree-node-content-wrapper,
  .ant-tree-node-content-wrapper-close,
  .ant-tree-node-selected {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
  }
  
  .ant-tree-title {
    flex: 1 !important;
    width: 100% !important;
  }
`;

// 注入样式
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = treeNodeStyles;
  document.head.appendChild(styleElement);
}

// sessionStorage 的 key 常量
const STORAGE_KEYS = {
  SELECTED_KEYS: 'intellix_menu_selected_keys',
  EXPANDED_KEYS: 'intellix_menu_expanded_keys',
  SELECTED_NODE: 'intellix_menu_selected_node',
};

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { shouldHideHeader } = useEmbedMode();

  const {
    ontologyList,
    setOntologyList,
    loading,
    loadResourceTree,
    hasOntologies,
    getDirectoryOptions,
  } = useResourceTree();

  const {
    ontologyDetail,
    setOntologyDetail,
    loadingDetail,
    loadOntologyDetail,
  } = useOntologyDetail();

  const [selectedOntology, setSelectedOntology] = useState<OntologyNode | null>(
    null
  );
  const [selectedNode, setSelectedNode] = useState<OntologyNode | null>(null);

  // 编辑状态管理（用于资源树中的编辑）
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // Tree组件选中状态管理
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  // Tree组件展开状态管理
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  // 跟踪用户是否主动清除了选择
  const [userClearedSelection, setUserClearedSelection] =
    useState<boolean>(false);

  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameNodeData, setRenameNodeData] = useState<OntologyNode | null>(
    null
  );
  const [renameValue, setRenameValue] = useState('');

  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [moveNodeData, setMoveNodeData] = useState<OntologyNode | null>(null);

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteNodeData, setDeleteNodeData] = useState<OntologyNode | null>(
    null
  );

  const [targetDirectoryId, setTargetDirectoryId] = useState<
    number | undefined
  >();

  // 标记是否已经恢复过状态，避免重复恢复
  const hasRestoredState = useRef(false);
  // 标记是否是从 sessionStorage 恢复的状态（初始化为 true，防止首次渲染时清空 sessionStorage）
  const isRestoringFromStorage = useRef(true);
  // 标记是否成功恢复了选中状态
  const hasRestoredSelection = useRef(false);

  // 从 sessionStorage 恢复状态
  useEffect(() => {
    // 只在 ontologyList 加载完成且未恢复过状态时执行
    if (ontologyList.length > 0 && !hasRestoredState.current && !loading) {
      hasRestoredState.current = true;
      isRestoringFromStorage.current = true;

      try {
        const savedSelectedKeys = sessionStorage.getItem(
          STORAGE_KEYS.SELECTED_KEYS
        );
        const savedExpandedKeys = sessionStorage.getItem(
          STORAGE_KEYS.EXPANDED_KEYS
        );
        const savedSelectedNode = sessionStorage.getItem(
          STORAGE_KEYS.SELECTED_NODE
        );

        // 辅助函数：递归查找节点
        const findNodeByKey = (
          nodes: OntologyNode[],
          key: string
        ): OntologyNode | null => {
          for (const node of nodes) {
            if (node.key === key) {
              return node;
            }
            if (node.children) {
              const found = findNodeByKey(node.children, key);
              if (found) return found;
            }
          }
          return null;
        };

        // 恢复展开状态
        if (savedExpandedKeys) {
          const expandedKeys = JSON.parse(savedExpandedKeys) as React.Key[];
          setExpandedKeys(expandedKeys);
        }

        // 恢复选中状态
        if (savedSelectedKeys && savedSelectedNode) {
          const selectedKeys = JSON.parse(savedSelectedKeys) as React.Key[];
          const nodeInfo = JSON.parse(savedSelectedNode);

          if (selectedKeys.length > 0) {
            const nodeKey = selectedKeys[0] as string;
            const node = findNodeByKey(ontologyList, nodeKey);

            if (node) {
              setSelectedKeys(selectedKeys);
              setSelectedNode(node);
              hasRestoredSelection.current = true; // 标记成功恢复了选中状态

              // 如果是本体节点，加载详情
              if (node.type === 'ontology') {
                setSelectedOntology(node);
                const ontologyId = node.ontologyId || node.id;
                if (ontologyId !== undefined) {
                  loadOntologyDetail(ontologyId);
                }
              } else if (node.type === 'folder') {
                setSelectedOntology(null);
                setOntologyDetail(null);
              }
            }
          }
        }
      } catch (error) {
        console.error('恢复菜单状态失败:', error);
      } finally {
        // 延迟重置标志，确保状态已经完全应用
        setTimeout(() => {
          isRestoringFromStorage.current = false;
        }, 100);
      }
    }
  }, [ontologyList, loading, loadOntologyDetail, setOntologyDetail]);

  // 初始化时设置默认文件夹为选中状态
  useEffect(() => {
    // 只有在没有选中项、用户没有主动清除选择、已加载完成且没有从 sessionStorage 恢复选中状态时，才自动选中默认文件夹
    if (
      selectedKeys.length === 0 &&
      !userClearedSelection &&
      !loading &&
      hasRestoredState.current &&
      !hasRestoredSelection.current
    ) {
      const findDefaultFolder = (
        nodes: OntologyNode[]
      ): OntologyNode | null => {
        for (const node of nodes) {
          if (node.type === 'folder' && node.title === 'Default') {
            return node;
          }
          if (node.children) {
            const found = findDefaultFolder(node.children);
            if (found) return found;
          }
        }
        return null;
      };

      const defaultFolder = findDefaultFolder(ontologyList);
      if (defaultFolder) {
        setSelectedKeys([defaultFolder.key]);
        setSelectedNode(defaultFolder);
      }
    }
  }, [ontologyList, selectedKeys.length, userClearedSelection, loading]);

  // 保存选中和展开状态到 sessionStorage
  useEffect(() => {
    // 跳过初始恢复时的保存
    if (isRestoringFromStorage.current) {
      return;
    }

    // 保存展开状态
    if (expandedKeys.length > 0) {
      sessionStorage.setItem(
        STORAGE_KEYS.EXPANDED_KEYS,
        JSON.stringify(expandedKeys)
      );
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.EXPANDED_KEYS);
    }
  }, [expandedKeys]);

  useEffect(() => {
    // 跳过初始恢复时的保存
    if (isRestoringFromStorage.current) {
      return;
    }

    // 保存选中状态
    if (selectedKeys.length > 0 && selectedNode) {
      sessionStorage.setItem(
        STORAGE_KEYS.SELECTED_KEYS,
        JSON.stringify(selectedKeys)
      );
      sessionStorage.setItem(
        STORAGE_KEYS.SELECTED_NODE,
        JSON.stringify({
          key: selectedNode.key,
          type: selectedNode.type,
          ontologyId: selectedNode.ontologyId,
          id: selectedNode.id,
        })
      );
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_KEYS);
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_NODE);
    }
  }, [selectedKeys, selectedNode]);

  const handleOntologySelect = (selectedKeys: React.Key[], info: any) => {
    // 更新选中状态，确保只高亮当前选择的节点
    setSelectedKeys(selectedKeys);

    if (selectedKeys.length > 0) {
      // 用户选择了某个节点，重置清除选择标志
      setUserClearedSelection(false);

      const nodeData = info.node.data;
      if (nodeData.type === 'ontology') {
        setSelectedOntology(nodeData);
        setSelectedNode(nodeData);

        if (nodeData.ontologyId || nodeData.id) {
          const ontologyId = nodeData.ontologyId || nodeData.id;
          loadOntologyDetail(ontologyId);
        }
      } else if (nodeData.type === 'folder') {
        // 如果点击的是已选中的文件夹，则取消选择
        if (selectedNode && selectedNode.key === nodeData.key) {
          setSelectedKeys([]);
          setSelectedNode(null);
          setSelectedOntology(null);
          setOntologyDetail(null);
          // 用户主动取消了选择
          setUserClearedSelection(true);
        } else {
          setSelectedNode(nodeData);
          setSelectedOntology(null);
          setOntologyDetail(null);
        }
      }
    } else {
      // 清除选中状态
      setSelectedOntology(null);
      setSelectedNode(null);
      setOntologyDetail(null);
      // 用户主动取消了选择
      setUserClearedSelection(true);
    }
  };

  // 添加新本体
  const handleAddOntology = () => {
    // 使用工具函数创建新节点
    const newNode = createNewNode('ontology', selectedNode, ontologyList);

    // 将新本体添加到正确的位置
    if (selectedNode) {
      // 有选中的节点，添加到正确位置
      setOntologyList(prev =>
        addNodeToCorrectPosition(prev, newNode, selectedNode, ontologyList)
      );
      // 确保父目录展开
      if (!expandedKeys.includes(selectedNode.key)) {
        setExpandedKeys([...expandedKeys, selectedNode.key]);
      }
    } else {
      // 没有选中节点，添加到默认文件夹下或根级别
      setOntologyList(prev =>
        addNodeToDefaultOrRoot(prev, newNode, expandedKeys, setExpandedKeys)
      );
    }

    setEditingKey(newNode.key);
    setEditingValue('');
  };

  // 添加新文件夹
  const handleAddFolder = () => {
    // 使用工具函数创建新节点
    const newNode = createNewNode('folder', selectedNode, ontologyList);

    // 将新文件夹添加到正确的位置
    if (selectedNode) {
      // 有选中的节点，添加到正确位置
      setOntologyList(prev =>
        addNodeToCorrectPosition(prev, newNode, selectedNode, ontologyList)
      );
      // 确保父目录展开
      if (!expandedKeys.includes(selectedNode.key)) {
        setExpandedKeys([...expandedKeys, selectedNode.key]);
      }
    } else {
      // 没有选中节点，添加到根级别
      setOntologyList(prev => [...prev, newNode]);
    }

    setEditingKey(newNode.key);
    setEditingValue('');
  };

  const handleShowRenameModal = (nodeData: OntologyNode) => {
    setRenameNodeData(nodeData);
    setRenameValue(nodeData.title);
    setRenameModalVisible(true);
  };

  const handleShowMoveModal = (nodeData: OntologyNode) => {
    setMoveNodeData(nodeData);
    setMoveModalVisible(true);
  };

  const handleShowDeleteModal = (nodeData: OntologyNode) => {
    setDeleteNodeData(nodeData);
    setDeleteModalVisible(true);
  };

  const handleRenameConfirm = async () => {
    const normalizedRenameValue = renameValue.trim();
    if (!normalizedRenameValue) {
      message.warning('名称不能为空');
      return;
    }

    if (normalizedRenameValue.length > RESOURCE_NAME_MAX_LENGTH) {
      message.warning(`名称不能超过${RESOURCE_NAME_MAX_LENGTH}个字`);
      return;
    }

    if (renameNodeData && normalizedRenameValue) {
      const success = await ResourceHelpers.renameResource(
        renameNodeData,
        normalizedRenameValue
      );
      if (success) {
        await loadResourceTree();
        setRenameModalVisible(false);
        setRenameNodeData(null);
        setRenameValue('');
      }
    }
  };

  const handleMoveConfirm = async () => {
    if (moveNodeData && moveNodeData.id && targetDirectoryId !== undefined) {
      const success = await ResourceHelpers.moveResource(
        moveNodeData.id,
        targetDirectoryId
      );
      if (success) {
        await loadResourceTree();
        setMoveModalVisible(false);
        setMoveNodeData(null);
        setTargetDirectoryId(undefined);
      }
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteNodeData) {
      const success = await ResourceHelpers.deleteResource(
        deleteNodeData,
        targetDirectoryId
      );
      if (success) {
        await loadResourceTree();
        if (selectedOntology?.key === deleteNodeData.key) {
          setSelectedOntology(null);
          setOntologyDetail(null);
        }
        if (selectedNode?.key === deleteNodeData.key) {
          setSelectedNode(null);
        }
        setDeleteModalVisible(false);
        setDeleteNodeData(null);
        setTargetDirectoryId(undefined);
      }
    }
  };

  const handleRenameCancel = () => {
    setRenameModalVisible(false);
    setRenameNodeData(null);
    setRenameValue('');
  };

  const handleMoveCancel = () => {
    setMoveModalVisible(false);
    setMoveNodeData(null);
    setTargetDirectoryId(undefined);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setDeleteNodeData(null);
    setTargetDirectoryId(undefined);
  };

  // 处理移动资源（拖拽操作）
  const handleMoveResource = async (
    sourceId: number,
    targetId: number
  ): Promise<boolean> => {
    try {
      const success = await ResourceHelpers.moveResource(sourceId, targetId);
      if (success) {
        await loadResourceTree();
        // 清除可能影响的选中状态
        setSelectedKeys([]);
        setSelectedOntology(null);
        setSelectedNode(null);
        setOntologyDetail(null);
      }
      return success;
    } catch (error) {
      return false;
    }
  };

  // 处理删除资源（拖拽删除）
  const handleDeleteResource = async (
    nodeData: OntologyNode
  ): Promise<boolean> => {
    try {
      // 拖拽删除目录且目录下有资源时，走与右键删除一致的确认弹窗流程
      if (
        nodeData.type === 'folder' &&
        nodeData.children &&
        nodeData.children.length > 0
      ) {
        setDeleteNodeData(nodeData);
        setTargetDirectoryId(undefined);
        setDeleteModalVisible(true);
        return true;
      }

      const success = await ResourceHelpers.deleteResource(nodeData);
      if (success) {
        await loadResourceTree();
        // 清除相关的选中状态
        if (selectedOntology?.key === nodeData.key) {
          setSelectedOntology(null);
          setOntologyDetail(null);
        }
        if (selectedNode?.key === nodeData.key) {
          setSelectedNode(null);
        }
        setSelectedKeys([]);
      }
      return success;
    } catch (error) {
      return false;
    }
  };

  return (
    <div className="h-full min-h-0 flex overflow-hidden">
      <ResourceTree
        ontologyList={ontologyList}
        setOntologyList={setOntologyList}
        loading={loading}
        selectedNode={selectedNode}
        onSelect={handleOntologySelect}
        onReload={loadResourceTree}
        onShowRenameModal={handleShowRenameModal}
        onShowMoveModal={handleShowMoveModal}
        onShowDeleteModal={handleShowDeleteModal}
        onAddOntology={handleAddOntology}
        onAddFolder={handleAddFolder}
        editingKey={editingKey}
        editingValue={editingValue}
        setEditingKey={setEditingKey}
        setEditingValue={setEditingValue}
        onConfirmEdit={undefined}
        onCancelEdit={undefined}
        selectedKeys={selectedKeys}
        setSelectedKeys={setSelectedKeys}
        onMoveResource={handleMoveResource}
        onDeleteResource={handleDeleteResource}
        expandedKeys={expandedKeys}
        setExpandedKeys={setExpandedKeys}
      />

      <div
        className="flex-1 min-h-0 flex flex-col overflow-hidden"
        style={{
          height: shouldHideHeader ? '100vh' : 'calc(100vh - 128px)',
          maxHeight: shouldHideHeader ? '100vh' : 'calc(100vh - 128px)',
        }}
      >
        {selectedOntology ? (
          <OntologyDetail
            ontologyDetail={ontologyDetail}
            loadingDetail={loadingDetail}
            onBack={() => {
              setSelectedOntology(null);
              setOntologyDetail(null);
              setSelectedKeys([]);
              setSelectedNode(null);
              hasRestoredSelection.current = false;
            }}
            loadOntologyDetail={loadOntologyDetail}
            onAddOntology={handleAddOntology}
            ontologyList={ontologyList}
            setOntologyList={setOntologyList}
            selectedNode={selectedNode}
            expandedKeys={expandedKeys}
            setExpandedKeys={setExpandedKeys}
            setEditingKey={setEditingKey}
            setEditingValue={setEditingValue}
          />
        ) : (
          <Overview
            hasOntologies={hasOntologies(ontologyList)}
            onAddOntology={handleAddOntology}
          />
        )}
      </div>

      <ActionModals
        renameModalVisible={renameModalVisible}
        renameNodeData={renameNodeData}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        onRenameConfirm={handleRenameConfirm}
        onRenameCancel={handleRenameCancel}
        moveModalVisible={moveModalVisible}
        moveNodeData={moveNodeData}
        targetDirectoryId={targetDirectoryId}
        setTargetDirectoryId={setTargetDirectoryId}
        moveDirectoryOptions={getDirectoryOptions(
          ontologyList,
          moveNodeData?.key
        )}
        deleteDirectoryOptions={getDirectoryOptions(
          ontologyList,
          deleteNodeData?.key
        )}
        onMoveConfirm={handleMoveConfirm}
        onMoveCancel={handleMoveCancel}
        deleteModalVisible={deleteModalVisible}
        deleteNodeData={deleteNodeData}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default Home;
